import { useCallback, useMemo, useState } from 'react'
import cx from 'classnames'

import styles from './Slider.module.css'

type Marks = Record<number, string>

type Props = {
  marks?: Marks
  step?: number
  defaultValue?: number
  disabled?: boolean
  onAfterChange?: (value: number) => void
  'data-testid'?: string
  [key: string]: unknown
}

const getMarkValues = (marks: Marks): number[] =>
  Object.keys(marks)
    .map(Number)
    .sort((a, b) => a - b)

const snapToMark = (value: number, markValues: number[]): number => {
  if (!markValues.length) {
    return value
  }
  return markValues.reduce((prev, curr) =>
    Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
  )
}

const Slider = ({
  marks,
  step,
  defaultValue = 0,
  disabled,
  onAfterChange,
  'data-testid': testId,
}: Props) => {
  const markValues = useMemo(() => (marks ? getMarkValues(marks) : []), [marks])
  const min = markValues[0] ?? 0
  const max = markValues[markValues.length - 1] ?? 100

  const [value, setValue] = useState(
    marks ? snapToMark(defaultValue, markValues) : defaultValue
  )

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = Number(e.target.value)
      const snapped = marks ? snapToMark(raw, markValues) : raw
      setValue(snapped)
    },
    [marks, markValues]
  )

  const handleCommit = useCallback(() => {
    onAfterChange?.(value)
  }, [value, onAfterChange])

  const percent = max > min ? ((value - min) / (max - min)) * 100 : 0

  return (
    <div className={cx('updated-component', styles.container)}>
      <input
        type="range"
        data-testid={testId}
        min={min}
        max={max}
        step={step ?? 1}
        value={value}
        disabled={disabled}
        onChange={handleChange}
        onMouseUp={handleCommit}
        onTouchEnd={handleCommit}
        className="w-full"
        style={{ accentColor: 'var(--color-maroon-400, #9F1239)' }}
      />
      {marks && (
        <div className="relative flex mt-1">
          {markValues.map((markVal) => {
            const pos = ((markVal - min) / (max - min)) * 100
            return (
              <span
                key={markVal}
                className="absolute text-xs text-coolGray-800 -translate-x-1/2"
                style={{ left: `${pos}%` }}
              >
                {marks[markVal]}
              </span>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default Slider
